<script setup lang="ts">
import {
  RefreshCw as RefreshCwIcon,
  ChevronRight as ChevronRightIcon,
  CircleAlert as CircleAlertIcon,
  CircleCheck as CircleCheckIcon,
  Image as ImageIcon,
  MapPin as MapPinIcon,
  Pencil as PencilIcon,
  Route as RouteIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Copy as CopyIcon,
} from '@lucide/vue'
import { useNow, useOnline } from '@vueuse/core'
import { computed, onBeforeUnmount, ref, watch, type FunctionalComponent } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseProgressBar from '@/components/ui/BaseProgressBar.vue'
import { useSyncDrainProgress } from '@/composables/useSyncDrainProgress'
import { connectivityService } from '@/services/connectivityService'
import { showConfirm } from '@/services/dialogService'
import { formatByteSize } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { deriveActivityItems, type ActivityItemKind } from '@/services/syncActivityService'
import {
  activityStatusLabels,
  discardConfirmCopy,
  humanizeSyncError,
  manualRetryFailedMessage,
  persistentFailureMessage,
  quarantineExplainer,
  quarantineHeadline,
  retryLabel,
  statusHeadline,
  syncToasts,
} from '@/services/syncCopy'
import { syncService } from '@/services/syncService'
import { useAuthStore } from '@/stores/authStore'
import { useEventStore } from '@/stores/eventStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const eventStore = useEventStore()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

// While the dialog is visible it shows every sync state in place — ambient toasts would be
// double feedback, so the service suppresses them meanwhile (sync-feedback.md §4.4).
watch(
  () => props.isOpen,
  (open) => {
    syncService.syncDialogVisible.value = open
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  syncService.syncDialogVisible.value = false
})

// Activity view (sync-feedback.md §4.2/§5): the queue as user-recognizable items
// ("12 photos → 'Alps 2026'", "Track 'Morning Ride'") instead of a bare count.
const resolveCollectionName = (clientId: string) =>
  clientId === workspaceStore.clientId ? (workspaceStore.name ?? undefined) : undefined

const activityItems = computed(() =>
  deriveActivityItems(eventStore.unsyncedEvents, {
    resolveCollectionName,
    inFlightEventIds: syncService.currentBatchClientIds.value,
    failedEventIds:
      syncStatus.value === 'error' ? syncService.lastFailedBatchClientIds.value : undefined,
  }),
)

// Quarantined (server-rejected) items: set aside so everything else syncs; per-item
// Try again / Discard (sync-feedback.md §6, quarantine v1).
const quarantinedItems = computed(() =>
  deriveActivityItems(eventStore.quarantinedEvents, { resolveCollectionName }).map((item) => ({
    ...item,
    status: 'failed' as const,
  })),
)
const quarantinedCount = computed(() => eventStore.quarantinedEvents.length)

const handleRetryQuarantined = async (eventClientIds: string[]) => {
  await eventStore.retryQuarantinedEvents(eventClientIds)
  await syncService.synchronize({ force: true })
}

const handleDiscardQuarantined = async (item: { label: string; eventClientIds: string[] }) => {
  const { title, message } = discardConfirmCopy(item.label)
  const confirmed = await showConfirm({ title, message, confirmText: 'Remove', variant: 'danger' })
  if (!confirmed) return

  await eventStore.discardQuarantinedEvents(item.eventClientIds)
  // Roll back the optimistic local state by reloading from the server — only safe once
  // nothing unsynced remains locally (otherwise the refresh would clobber queued work).
  if (
    eventStore.unsyncedEvents.length === 0 &&
    eventStore.quarantinedEvents.length === 0 &&
    workspaceStore.clientId
  ) {
    await syncService.refreshCurrentRemoteWorkspace(workspaceStore.clientId)
  }
}

const activityIcons: Record<ActivityItemKind, FunctionalComponent> = {
  photos: ImageIcon,
  'photo-changes': ImageIcon,
  track: RouteIcon,
  places: MapPinIcon,
  collection: PencilIcon,
  'route-changes': RouteIcon,
  other: PencilIcon,
}

// Use vueuse online status
const isOnline = useOnline()
const isForceOfflineSupported = computed(() => connectivityService.forceOfflineSupported)
const isForcedOffline = computed(() => connectivityService.forceOfflineMode.value)
const isEffectivelyOnline = computed(() => connectivityService.isEffectivelyOnline.value)
const onlineStatusLabel = computed(() => {
  if (!isOnline.value) return 'Offline'
  if (isForceOfflineSupported.value && isForcedOffline.value) return 'Offline mode'
  return 'Online'
})
const onlineStatusClass = computed(() => {
  if (!isOnline.value) return 'text-gray-600'
  if (isForceOfflineSupported.value && isForcedOffline.value) return 'text-amber-700'
  return 'text-green-700'
})
const onlineStatusIconClass = computed(() => {
  if (!isOnline.value) return 'w-4 h-4 text-gray-500'
  if (isForceOfflineSupported.value && isForcedOffline.value) return 'w-4 h-4 text-amber-600'
  return 'w-4 h-4 text-green-600'
})

const syncLogs = computed(() => logService.getLogs())
const unsyncedCount = computed(() => eventStore.unsyncedEvents.length)
const hasUnsyncedEvents = computed(() => unsyncedCount.value > 0)
const unsyncedEventsSize = computed(() => {
  const totalSize = eventStore.unsyncedEvents.reduce(
    (sum, event) => sum + (event._sizeBytes || 0),
    0,
  )
  return formatByteSize(totalSize)
})

const syncStatus = computed(() => syncService.syncStatus.value)

// Signed out with queued work is its own actionable state (sync-feedback.md §9): the fix is
// signing in, not retrying — show the action instead of a dead Sync now button.
const isSignedOutWithQueue = computed(() => !authStore.isAuthenticated && unsyncedCount.value > 0)
const handleSignIn = () => {
  emit('close')
  window.dispatchEvent(new CustomEvent('open-auth-modal'))
}

const headline = computed(() => {
  if (isSignedOutWithQueue.value) return 'Signed out'
  const base = statusHeadline(syncStatus.value, unsyncedCount.value)
  // "Everything synced" next to a red rejected-items section would be contradictory
  return quarantinedCount.value > 0 && base === 'Everything synced'
    ? 'Everything else synced'
    : base
})
const errorText = computed(() => {
  if (isSignedOutWithQueue.value) return syncToasts.signedOut
  if (syncStatus.value !== 'error') return null
  // After a full failed retry cycle, "will retry automatically" is no longer a credible
  // promise — switch to honest, actionable copy (sync-feedback.md §6).
  if (syncService.persistentFailure.value) return persistentFailureMessage
  return humanizeSyncError(syncService.lastErrorMessage.value, authStore.isAuthenticated)
})
const isPersistentFailure = computed(
  () => syncStatus.value === 'error' && syncService.persistentFailure.value,
)

// Inline feedback for a manual "Sync now" that failed immediately — a fast server rejection
// restores the identical card within milliseconds, so without this line the button looks dead.
// (Rendered in place; no toast — the dialog is already open.)
const manualAttemptFailed = ref(false)
watch(syncStatus, (status) => {
  if (status !== 'error') {
    manualAttemptFailed.value = false
  }
})

// Live clock — drives the retry countdown.
const now = useNow({ interval: 1000 })

// Queue-drain progress ("66% · 4 of 11 synced — uploading…"), shared with the ambient
// top-edge progress bar.
const { drainProgress } = useSyncDrainProgress()
const retryText = computed(() => {
  if (syncStatus.value !== 'error') return null
  if (syncService.nextRetryAt.value !== null) {
    const seconds = (syncService.nextRetryAt.value - now.value.getTime()) / 1000
    return retryLabel(seconds, syncService.retryAttempt.value, syncService.retryMaxAttempts)
  }
  if (syncService.cooldownUntil.value !== null) {
    return retryLabel(null, 0, syncService.retryMaxAttempts)
  }
  return null
})

// Technical details (log console, force sync/offline, raw error) are demoted behind a
// collapsed disclosure — support conversations still need them, normal users never do.
const showSupportDetails = ref(false)

// Format time as logfile timestamp helper
const formatLogTime = (timestamp: number) => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

// Handle "Sync now": a manual attempt that fails instantly (fast server rejection) would
// leave the dialog looking untouched — say explicitly, in place, that it was tried and failed.
const handleForceSync = async () => {
  manualAttemptFailed.value = false
  const succeeded = await syncService.synchronize({ force: true })
  if (!succeeded && syncService.syncStatus.value === 'error') {
    manualAttemptFailed.value = true
  }
}

const handleToggleForceOfflineMode = (checked: boolean) => {
  connectivityService.setForceOfflineMode(checked)
}

// Handle copy sync log to clipboard
const handleCopyLog = async () => {
  const logText = logService.exportLogsAsText(50)

  try {
    await navigator.clipboard.writeText(logText)
    // You could add a toast notification here if available
  } catch (error) {
    // Log and fallback for older browsers
    logService.warn(
      LOG_CATEGORIES.UI,
      'Clipboard write failed in SyncStatusDialog; using textarea fallback',
      {
        error,
      },
    )
    const textArea = document.createElement('textarea')
    textArea.value = logText
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

// Handle close
const handleClose = () => {
  emit('close')
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    title="Sync status"
    description="See what's waiting to sync from this device"
    size="2xl"
    show-footer
    :content-class="'max-h-[90vh] overflow-hidden'"
    :body-class="'overflow-y-auto max-h-[75vh] p-4'"
    @close="handleClose"
  >
    <template #header-extra>
      <!-- Online Status in header -->
      <div class="flex items-center gap-2 text-sm mt-1">
        <component
          :is="isOnline && !isForcedOffline ? WifiIcon : WifiOffIcon"
          :class="onlineStatusIconClass"
        />
        <span :class="onlineStatusClass">
          {{ onlineStatusLabel }}
        </span>
      </div>
    </template>

    <!-- Current Status -->
    <div class="mb-6">
      <div
        :class="[
          // Layout
          'flex items-center gap-3',
          // Visual
          'p-3 rounded-md border',
          // Status-based styling — offline/waiting is calm gray, never red (waiting is normal);
          // signed-out is amber: actionable, but not a failure
          ...(isSignedOutWithQueue
            ? ['bg-amber-50', 'border-amber-200']
            : syncStatus === 'syncing'
              ? ['bg-blue-50', 'border-blue-200']
              : syncStatus === 'error'
                ? ['bg-red-50', 'border-red-200']
                : syncStatus === 'success'
                  ? ['bg-green-50', 'border-green-200']
                  : ['bg-gray-50', 'border-gray-200']),
        ]"
      >
        <component
          :is="
            isSignedOutWithQueue
              ? CircleAlertIcon
              : syncStatus === 'syncing'
                ? RefreshCwIcon
                : syncStatus === 'error'
                  ? CircleAlertIcon
                  : syncStatus === 'success'
                    ? CircleCheckIcon
                    : hasUnsyncedEvents
                      ? RefreshCwIcon
                      : CircleCheckIcon
          "
          :class="[
            'w-5',
            'h-5',
            ...(!isSignedOutWithQueue && syncStatus === 'syncing' ? ['animate-spin'] : []),
            ...(isSignedOutWithQueue
              ? ['text-amber-600']
              : syncStatus === 'syncing'
                ? ['text-blue-600']
                : syncStatus === 'error'
                  ? ['text-red-600']
                  : syncStatus === 'success'
                    ? ['text-green-600']
                    : ['text-gray-600']),
          ]"
        />
        <div class="flex-1">
          <p
            :class="[
              'text-sm font-medium',
              ...(isSignedOutWithQueue
                ? ['text-amber-900']
                : syncStatus === 'syncing'
                  ? ['text-blue-900']
                  : syncStatus === 'error'
                    ? ['text-red-900']
                    : syncStatus === 'success'
                      ? ['text-green-900']
                      : ['text-gray-900']),
            ]"
            data-testid="sync-status-headline"
          >
            {{ headline }}
          </p>
          <p
            v-if="errorText"
            :class="['text-xs mt-1', isSignedOutWithQueue ? 'text-amber-800' : 'text-red-700']"
          >
            {{ errorText }}
          </p>
          <!-- Signed out: the fix is signing in — show that action, not a dead Sync now -->
          <button
            v-if="isSignedOutWithQueue"
            type="button"
            class="mt-2 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 hover:cursor-pointer"
            data-testid="sync-sign-in"
            @click="handleSignIn"
          >
            Sign in
          </button>
          <p
            v-if="retryText && !isSignedOutWithQueue"
            class="text-xs mt-1 text-amber-700"
            data-testid="sync-retry-label"
          >
            {{ retryText }}
          </p>
          <!-- Manual Sync now that failed instantly: say so in place, no toast needed -->
          <p
            v-if="manualAttemptFailed"
            class="text-xs mt-1 font-medium text-red-800"
            data-testid="sync-manual-retry-failed"
          >
            {{ manualRetryFailedMessage }}
          </p>
          <!-- Persistent failure: give the user the actions that exist instead of a promise
               (sync-feedback.md §6 — per-item Retry/Discard arrives with the quarantine work) -->
          <button
            v-if="isPersistentFailure"
            type="button"
            class="mt-2 flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 hover:cursor-pointer"
            data-testid="sync-copy-report"
            @click="handleCopyLog"
          >
            <CopyIcon class="w-3 h-3" />
            Copy report for support
          </button>
          <!-- Queue-drain progress: real "how much is done" feedback (sync-feedback.md §4.2) -->
          <div v-if="drainProgress" class="mt-2" data-testid="sync-drain-progress">
            <BaseProgressBar :value="drainProgress.percent" />
            <p class="mt-1 text-xs text-gray-600">
              <span class="font-semibold tabular-nums"
                >{{ Math.round(drainProgress.percent) }}%</span
              >
              · {{ drainProgress.done }} of {{ drainProgress.total }} synced<template
                v-if="drainProgress.phase"
              >
                — {{ drainProgress.phase }}</template
              >
            </p>
          </div>
        </div>
      </div>

      <!-- Needs attention — server-rejected items set aside so everything else syncs;
           per-item Try again / Discard (sync-feedback.md §6, quarantine v1) -->
      <div
        v-if="quarantinedItems.length > 0"
        class="mt-3 rounded-md border border-red-200 bg-red-50 p-3"
        data-testid="sync-quarantine-section"
      >
        <p class="text-sm font-medium text-red-900">
          {{ quarantineHeadline(quarantinedCount) }}
        </p>
        <p class="mt-1 text-xs text-red-700">{{ quarantineExplainer }}</p>
        <ul class="mt-2 divide-y divide-red-100 rounded-md border border-red-200 bg-white">
          <li
            v-for="item in quarantinedItems"
            :key="item.key"
            class="flex flex-wrap items-center gap-2 px-3 py-2"
          >
            <component :is="activityIcons[item.kind]" class="h-4 w-4 shrink-0 text-red-400" />
            <span class="min-w-0 flex-1 truncate text-sm text-gray-800">{{ item.label }}</span>
            <div class="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                class="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:cursor-pointer"
                data-testid="quarantine-retry"
                @click="handleRetryQuarantined(item.eventClientIds)"
              >
                Try again
              </button>
              <button
                type="button"
                class="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 hover:cursor-pointer"
                data-testid="quarantine-discard"
                @click="handleDiscardQuarantined(item)"
              >
                Discard
              </button>
            </div>
          </li>
        </ul>
      </div>

      <!-- Activity list — the queue as user-recognizable items, not counts or events
           (sync-feedback.md §4.2). Always sums to the queue via the catch-all group. -->
      <ul
        v-if="activityItems.length > 0"
        class="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200 bg-white"
        data-testid="sync-activity-list"
      >
        <li v-for="item in activityItems" :key="item.key" class="flex items-center gap-3 px-3 py-2">
          <component :is="activityIcons[item.kind]" class="h-4 w-4 shrink-0 text-gray-400" />
          <span class="min-w-0 flex-1 truncate text-sm text-gray-800">{{ item.label }}</span>
          <span
            :class="[
              'shrink-0 text-xs',
              item.status === 'problem'
                ? 'font-medium text-red-600'
                : item.status === 'syncing'
                  ? 'font-medium text-blue-600'
                  : 'text-gray-400',
            ]"
          >
            {{ activityStatusLabels[item.status] }}
          </span>
        </li>
      </ul>

      <!-- Offline mode — a first-class travel feature, not a support tool (sync-feedback.md Q5):
           deliberately use downloaded content and pause syncing. -->
      <div
        v-if="isForceOfflineSupported"
        class="mt-4 rounded-md border border-gray-200 bg-white p-3"
      >
        <label class="flex items-start justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-gray-900">Offline mode</p>
            <p class="mt-1 text-xs text-gray-500">
              Use downloaded content and pause syncing — even while the device still has a slow or
              flaky connection. Your changes stay saved on this device.
            </p>
          </div>
          <BaseCheckbox
            :model-value="isForcedOffline"
            class="mt-0.5"
            @update:model-value="handleToggleForceOfflineMode"
          />
        </label>
      </div>
    </div>

    <!-- Sync log (collapsed by default): raw state + log console for support cases -->
    <div class="mb-4 rounded-md border border-gray-200">
      <button
        type="button"
        class="flex w-full items-center gap-2 p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:cursor-pointer"
        data-testid="sync-support-details-toggle"
        :aria-expanded="showSupportDetails"
        @click="showSupportDetails = !showSupportDetails"
      >
        <ChevronRightIcon
          :class="['w-4 h-4 transition-transform', showSupportDetails ? 'rotate-90' : '']"
        />
        Sync log
      </button>

      <div v-if="showSupportDetails" class="border-t border-gray-200 p-3 space-y-3">
        <!-- Header row: raw state left, Copy action right — one line, no floating elements -->
        <div class="flex items-start justify-between gap-3">
          <div class="text-xs text-gray-500 space-y-1">
            <p>Status: {{ syncStatus }}</p>
            <p v-if="hasUnsyncedEvents">
              {{ unsyncedCount }} unsynced events ({{ unsyncedEventsSize }})
            </p>
            <p v-if="syncService.lastErrorMessage.value">
              Last error: {{ syncService.lastErrorMessage.value }}
            </p>
          </div>
          <button
            v-if="syncLogs.length > 0"
            @click="handleCopyLog"
            :class="[
              // Layout
              'flex items-center gap-1 shrink-0',
              // Sizing
              'px-2 py-1',
              // Typography
              'text-xs font-medium',
              // Visual
              'text-gray-600 border border-gray-300 bg-white rounded-md',
              // Interactive
              'hover:bg-gray-50 hover:text-gray-700 hover:cursor-pointer',
              // Transitions
              'transition-colors duration-150',
            ]"
            title="Copy sync log to clipboard"
          >
            <CopyIcon class="w-3 h-3" />
            <span>Copy</span>
          </button>
        </div>

        <!-- Log console -->
        <div>
          <div v-if="syncLogs.length === 0" class="text-sm text-gray-500">
            No log entries available
          </div>
          <div
            v-else
            :class="[
              // Logfile styling
              'bg-gray-900 text-gray-100 rounded-md p-3',
              // Typography
              'text-xs leading-tight',
              // Scrollable area
              'max-h-96 overflow-y-auto',
              // Scrollbar styling
              'scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500',
              // Border
              'border border-gray-300',
            ]"
          >
            <div
              v-for="entry in syncLogs.slice(0, 50)"
              :key="entry.id"
              :class="[
                // Level-based color
                ...(entry.level === 'success'
                  ? ['text-green-400']
                  : entry.level === 'info'
                    ? ['text-blue-400']
                    : entry.level === 'warn'
                      ? ['text-yellow-400']
                      : entry.level === 'error'
                        ? ['text-red-400']
                        : ['text-gray-400']),
              ]"
            >
              <!-- Main log entry -->
              <div
                :class="[
                  // Layout
                  'flex items-center gap-2 py-0.5',
                  // Interactive
                  'hover:bg-gray-800 hover:bg-opacity-50',
                  // Transitions
                  'transition-colors duration-150',
                ]"
              >
                <!-- Timestamp -->
                <span class="text-gray-500 shrink-0">{{ formatLogTime(entry.timestamp) }}</span>

                <!-- Level indicator -->
                <span class="shrink-0 min-w-[4rem] text-left font-semibold">
                  {{ entry.level.toUpperCase() }}
                </span>

                <!-- Category -->
                <span class="text-gray-400 shrink-0 min-w-[3rem] text-left">
                  {{ entry.category.toUpperCase() }}
                </span>

                <!-- Message -->
                <span class="flex-1 truncate text-gray-300">
                  {{ entry.message }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        @click="handleClose"
        :class="[
          // Layout
          'flex items-center gap-2',
          // Sizing
          'px-4 py-2',
          // Typography
          'text-sm font-medium',
          // Visual
          'text-gray-700 border border-gray-300 bg-white rounded-md',
          // Interactive
          'hover:bg-gray-50 hover:cursor-pointer',
        ]"
      >
        Close
      </button>
      <button
        :disabled="
          syncStatus === 'syncing' ||
          !isEffectivelyOnline ||
          !hasUnsyncedEvents ||
          !authStore.isAuthenticated
        "
        data-testid="sync-now-button"
        :class="[
          // Layout
          'flex items-center gap-2',
          // Sizing
          'px-4 py-2',
          // Typography
          'text-sm font-medium',
          // Visual
          'text-white bg-primary-600 rounded-md',
          // Interactive
          'hover:bg-primary-700 hover:cursor-pointer',
          // Disabled
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600',
        ]"
        @click="handleForceSync"
      >
        <RefreshCwIcon
          :class="['w-4 h-4', ...(syncStatus === 'syncing' ? ['animate-spin'] : [])]"
        />
        Sync now
      </button>
    </template>
  </BaseModal>
</template>
